<template>
  <div class="resultsBox" id="resultsBox">
    <div v-if="hasResults">
      <div class="results" >
        <div class="results__title-wrapper">
        <div class="scrollDownArrows" id="scrollDownArrows">
          <img class="scroll-down-icon" :src="`${baseURL}static/img/scroll_down.png`" />
        </div>
          <af-h2 class="results__title">
            <span class="results__title-matched">
              {{ results.total }}
            </span>
            &nbsp;kandidater matchar
          </af-h2>

          <div class="results__sort">
            <search-sort />
          </div>
        </div>

        <ul>
          <router-link
            v-for="result in results.hits"
            :key="result.id"
            :to="'/profile/' + result.id"
            class="result"
            :target="targetDestination">
            <div class="result__avatar">
              <img class="result__avatar-image"  :src="`${baseURL}static/img/avatar.svg`" />
            </div>

            <ul class="result__matches">
              <li v-if="result.city" class="result__match">
                <icon class="result__icon-dot" link :size="10" type="circle"></icon>
                <strong class="result__label">Bor:</strong> {{ result.city }}
              </li>
              <li
                v-for="(match, i) in matchedRequirements(result.matches)"
                :key="i"
                class="result__match">
                <icon class="result__icon" fill="rgb(72, 134, 0)" :size="18" type="check"></icon>
                <strong class="result__label">{{ match.type }}:</strong> {{ match.text }}
              </li>
            </ul>
          </router-link>
        </ul>

        <search-pagination />
      </div>
    </div>

    <div v-if="hasNoResults && !noResults" class="no-results">
      <af-h2 noMarginTop>Sökresultat saknas</af-h2>
      <p>Tyvärr, vi kan inte hitta några kandidater som matchar din sökning. Prova att utöka din sökning genom att ta bort kriterier.</p>
    </div>

     <div v-if="hasError" class="no-results">
      <af-h2 noMarginTop>Ett fel uppstod</af-h2>
    </div>

  </div>
</template>

<script>
import { mapState } from 'vuex'
import Icon from '@/components/Icon'
import SearchPagination from './SearchPagination'
import SearchSort from './SearchSort'
import AfH2 from '@/components/AfH2'
import { isIE11 } from '@/utils/helpers'

export default {
  name: 'SearchResults',
  components: {
    Icon,
    SearchPagination,
    SearchSort,
    AfH2
  },

  computed: {
    ...mapState(['results', 'hasSearched', 'noResults']),
    hasResults() {

      // toggle 'stuck' on resultsBox
      const resultsBoxSection = document.getElementById("resultsBox")
      if (resultsBoxSection) {
        const observer = new IntersectionObserver(
          ([e]) => e.target.toggleAttribute('stuck', e.intersectionRatio < 1),
          { threshold: [1] }
        )
        observer.observe(resultsBoxSection)

        // TODO: enhanced method...
        let cardHeight = 180
        if (window.innerWidth < 768) {
          cardHeight = 120
        }
        resultsBoxSection.style.bottom = '-' + (this.results.total * cardHeight) + 'px'
      }

      return this.results.total > 0
    },
    hasNoResults() {
      return this.hasSearched && this.results.total === 0
    },
    targetDestination() {
        return isIE11() ? "_blank" : "_self"
    },
    hasError() {
      return this.$store.getters.hasError
    }
  },

  data() {
    return {
      baseURL: window.__BASE_URL__
    }
  },

  methods: {
    matchedRequirements(matches) {
      return matches.filter(match => match.isMatch)
    }
  }
}
</script>

<style lang="postcss" scoped>
@import '../../variables/custom-media.css';

.results {
  margin-bottom: 20px;

  a { text-decoration: none; }
}

.resultsBox {
  position: sticky;
  position:-webkit-sticky;
  z-index: 2;
  background-color: var(--color-white);
  border-top: 1px solid rgba(0, 0, 0, 0.15);

  &:hover {
    border-color: var(--color-alto);
    box-shadow: 0 3px 3px 2px rgba(0, 0, 0, 0.15);
  }

  @media (--bp-nis-extra-small) {
    bottom: -120px;
  }
}

.resultsBox[stuck] {
  border-top: 2px solid var(--color-stratos);
}

.scrollDownArrows {
  visibility: hidden;
  position: absolute;
  right: 20px;
}

.scrollDownArrows[stuck] {
  visibility: visible;
}

.scroll-down-icon {
  height: 20px;
}

.results__title-wrapper {
  margin-bottom: var(--spacing-medium);
  background: #fff;

  @media (--bp-desktop) {
    align-items: center;
    display: flex;
    justify-content: space-between;
    margin-bottom: 0;
    padding-left: 15px;
  }
}

.results__title {
  color: #000;
  font-size: 24px;
  font-weight: 300;

  @media (--bp-desktop) {
    font-size: 29px;
  }
}

.results__title-matched {
  color: var(--color-limeade);
  font-weight: 600;
}

.results__sort {
  display: none;
}

.result {
  background-color: var(--color-white);
  border-radius: 3px;
  box-shadow: 0 1px 1px rgba(0, 0, 0, 0.25);
  color: #000;
  cursor: pointer;
  display: grid;
  grid-template-columns: 1fr;
  padding: var(--spacing-half) var(--spacing-half) var(--spacing);
  transition: background-color 150ms ease-in-out, border-color 150ms ease-in-out;

  &:not(:last-child) {
    margin-bottom: var(--spacing-medium);
  }

  &:hover {
    border-color: var(--color-alto);
    box-shadow: 0 3px 3px 1px rgba(0, 0, 0, 0.15);
  }

  @media (--bp-nis-small) {
    grid-column-gap: var(--spacing-huge);
    grid-template-columns: 110px 1fr;
    padding: var(--spacing) var(--spacing-medium) var(--spacing) var(--spacing);
  }

  @media (--ie11) {
    display: block;
  }

  @media (--ie11) and (--bp-desktop) {
    display: -ms-grid;
    -ms-grid-columns: 110px 60px 1fr;
    -ms-grid-rows: 1fr;
  }
}

.result__avatar {
  margin-bottom: var(--spacing-medium);

  @media (--ie11) {
    -ms-grid-column: 1;
    -ms-grid-row: 1;
  }

  @media (--bp-nis-small) {
    margin-bottom: 0;
  }
}

.result__avatar-image {
  max-width: 39px;

  @media (--bp-nis-small) {
    max-width: 100%;
  }
}

.result__matches {
  display: grid;
  grid-template-columns: 1fr;

  @media (--bp-desktop) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (--ie11) and (--bp-desktop) {
    display: flex;
    flex-wrap: wrap;
    -ms-grid-column: 3;
    -ms-grid-row: 1;
    -ms-grid-row-align: start;
  }
}

.result__match {
  &:not(:last-child) {
    margin-bottom: var(--spacing-third);
  }

  @media (--ie11) {
    width: 100%;
  }

  @media (--ie11) and (--bp-desktop) {
    width: calc(50% - 20px);
  }
}

.result__label {
  text-transform: uppercase;
}

.result__icon {
  margin-right: var(--spacing-third);
}

.no-results {
  background-color: #fff;
}

.result__icon-dot {
  margin-left: 3px;
  margin-right: 13px;
}

.suggestions {
  display: flex;
  justify-content: space-between;
}

.suggestions__box {
  background: var(--color-white);
  border-radius: 3px;
  box-shadow: 0px 1px 1px rgba(0, 0, 0, 0.25);
  color: #000;
  margin-bottom: var(--spacing);
  padding: var(--spacing) var(--spacing-medium);
}

.no-results {
  margin-bottom: var(--spacing);
  padding: 40px var(--spacing);
}

</style>



// WEBPACK FOOTER //
// src/views/Search/SearchResults.vue